import {Injectable, inject} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {filter, take} from 'rxjs';
import {User} from '../core/model/user';
import {selectActiveUser} from '../core/store/active-user/active-user.selector';
import {UpdateUserAction} from '../core/store/users/users.action';
import {TimezoneWarningDialogComponent} from '../shared/dialog/timezone-warning-dialog/timezone-warning-dialog.component';
import {MessageService} from './message.service';
import {GetActiveUserAction} from '../core/store/active-user/active-user.action';
import {MobileService} from 'src/app/core/page/mobile.service';

@Injectable({
  providedIn: 'root',
})
export class TimezoneCheckerService {
  private readonly mobileService = inject(MobileService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);

  checkTimezone(): void {
    const currentTimezone = moment.tz.guess();

    this.store
      .select(selectActiveUser)
      .pipe(
        filter((user): user is User => !!user),
        take(1)
      )
      .subscribe(user => {
        if (!user.timezone) {
          this.updateUserTimezone(user, currentTimezone);
          return;
        }
        if (user.timezone && user.timezone !== currentTimezone) {
          this.openTimezoneWarningDialog(currentTimezone, user.timezone);
        }
      });
  }

  private openTimezoneWarningDialog(detectedTimezone: string, currentTimezone: string): void {
    this.dialog.open(TimezoneWarningDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: {
        newTimezone: detectedTimezone,
        currentTimezone: currentTimezone,
      },
    });
  }

  private updateUserTimezone(user: User, newTimezone: string): void {
    const updatedUser: User = {
      ...user,
      timezone: newTimezone,
    };

    this.store.dispatch(
      new UpdateUserAction({
        user: updatedUser,
        onSuccess: (user: User) => this.onUpdateSuccess(user),
        onFailure: () => this.onUpdateFailure(),
      })
    );
  }

  private onUpdateSuccess(user: User): void {
    this.store.dispatch(
      new GetActiveUserAction({
        onSuccess: () => this.onGetActiveUserSuccess(),
        onFailure: () => {
          this.onGetActiveUserFailure();
        },
      })
    );
  }

  private onUpdateFailure(): void {
    this.messageService.add('Failed to update timezone. Please try again.', 'error');
  }

  private onGetActiveUserSuccess(): void {
    this.checkTimezone();
  }

  private onGetActiveUserFailure(): void {
    this.messageService.add('Failed to get active user. Please try again.', 'error');
  }
}
